import React, { useEffect, useMemo } from 'react';
import { Link as RouterLink, useLocation } from 'react-router';
import { styled, useTheme } from '@mui/material/styles';
import { Box, Drawer, IconButton, Tooltip, useMediaQuery } from '@mui/material';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import useLocales from '../../hooks/useLocales';
import useAuth from '../../hooks/useAuth';
import { PATH_DASHBOARD, PATH_ORG } from '../../routes/paths';
import Logo from '../../components/Logo';
import LogoIsoMd from '../../components/LogoIsoMd';
import Scrollbar from '../../components/Scrollbar';
import NavSection from '../../components/NavSection';
import { useSelector } from '../../redux/store';
import type { RootState } from '../../redux/store';
import buildNavConfig from './SidebarConfig';

type NavConfig = {
  subheader: string;
  items: {
    title: string;
    path?: string;
    icon?: React.JSX.Element;
    exact?: boolean;
    info?: React.JSX.Element;
  }[];
}[];

const DRAWER_WIDTH = 260;
const DRAWER_WIDTH_COLLAPSED = 88;

const RootStyle = styled('div', {
  shouldForwardProp: (prop) => prop !== 'collapsed'
})<{ collapsed?: boolean }>(({ theme, collapsed }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    width: collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  }
}));

type DashboardSidebarProps = {
  isOpenSidebar: boolean;
  onCloseSidebar: VoidFunction;
  isCollapsed: boolean;
  onToggleCollapse: VoidFunction;
  navConfig?: NavConfig;
  footer?: React.ReactNode;
};

export default function DashboardSidebar({
  isOpenSidebar,
  onCloseSidebar,
  isCollapsed,
  onToggleCollapse,
  navConfig: navConfigProp,
  footer
}: DashboardSidebarProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { pathname } = useLocation();
  const { isAuthenticated } = useAuth();
  const { translate } = useLocales();

  const activeOrgGuid = useSelector((state: RootState) => state.organizationContext.activeOrgGuid);

  const logoPath = !isAuthenticated
    ? '/'
    : activeOrgGuid
      ? PATH_ORG.overview
      : PATH_DASHBOARD.overview.root;

  const enrichedConfig = useMemo(() => navConfigProp ?? buildNavConfig([]), [navConfigProp]);

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderHeader = (collapsed = false) =>
    collapsed ? (
      <Box
        sx={{
          pt: 1.5,
          pb: 0,
          pl: 3,
          pr: 0.5,
          minHeight: 52,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}
      >
        <Box component={RouterLink} to={logoPath} sx={{ display: 'flex', alignItems: 'center' }}>
          <LogoIsoMd />
        </Box>
        {isDesktop && (
          <Tooltip title={translate('menu.expand_sidebar')} placement="right">
            <IconButton size="small" onClick={onToggleCollapse} sx={{ color: 'text.secondary' }}>
              <KeyboardDoubleArrowRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    ) : (
      <Box
        sx={{
          pt: 1.5,
          pb: 0,
          pl: 2.5,
          pr: 1.5,
          minHeight: 52,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}
      >
        <Box component={RouterLink} to={logoPath} sx={{ display: 'inline-flex' }}>
          <Logo />
        </Box>
        {isDesktop && (
          <Tooltip title={translate('menu.collapse_sidebar')} placement="right">
            <IconButton size="small" onClick={onToggleCollapse} sx={{ color: 'text.secondary' }}>
              <KeyboardDoubleArrowLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );

  const renderContent = (collapsed = false) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {renderHeader(collapsed)}
      <Scrollbar
        sx={{
          flexGrow: 1,
          '& .simplebar-content': { display: 'flex', flexDirection: 'column' }
        }}
      >
        <NavSection navConfig={enrichedConfig} collapsed={collapsed} />
      </Scrollbar>
      {footer && <Box sx={{ px: 2, pb: 2, pt: 1, flexShrink: 0 }}>{footer}</Box>}
    </Box>
  );

  return (
    <RootStyle collapsed={isDesktop ? isCollapsed : false}>
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          slotProps={{
            paper: { sx: { width: DRAWER_WIDTH } }
          }}
        >
          {renderContent(false)}
        </Drawer>
      )}
      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          slotProps={{
            paper: {
              sx: {
                width: isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
                bgcolor: 'background.paper',
                overflowX: 'hidden',
                transition: theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen
                })
              }
            }
          }}
        >
          {renderContent(isCollapsed)}
        </Drawer>
      )}
    </RootStyle>
  );
}
